<template>
  <div class="gl-code-inline-edit flex flex-column align-items-start">
    <Chip
      v-tooltip="{
        value: internalValue.description,
        disabled: !internalValue.description,
      }"
      class="gl-code-chip"
      :class="{ 'auto-gl-code': internalValue.autoGLCode }"
      :removable="editable"
      @remove="emit('remove', internalValue)"
    >
      <PopupGLCodeDropdown
        :model-value="internalValue"
        :options="options"
        :disabled="!editable"
        :editable="$hasAuthority('glCode', 'manualInput')"
        @update:model-value="onUpdate"
      >
        <template #placeholder>
          <div class="gl-code-inline-edit-placeholder">
            {{ internalValue?.code }}
          </div>
        </template>
      </PopupGLCodeDropdown>
    </Chip>

    <RygenButton
      v-if="internalValue?.suggestion && editable"
      class="suggestion-button"
      mode="link"
      :label="
        t('common.useSuggestedParam', {
          param: internalValue.suggestion,
        })
      "
      @click="onAcceptSuggestion"
    />
  </div>
</template>

<script lang="ts" setup>
import { usePrimeVue } from 'primevue/config';
import { PropType, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { useModelValue, useTooltip } from '@rygen/rygen-ui/composables';
import { GLCode } from '@rygen/rygen-ui/model';

import PopupGLCodeDropdown from '@/components/glCode/PopupGLCodeDropdown.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import Chip from 'primevue/chip';

const props = defineProps({
  modelValue: {
    type: GLCode,
    default: () => new GLCode(),
  },
  options: {
    type: Array as PropType<GLCode[]>,
    default: () => [],
  },
  editable: {
    type: Boolean,
    default: () => true,
  },
});
const { modelValue, options, editable } = toRefs(props);
const emit = defineEmits<{
  (event: 'remove', glCode: GLCode): void;
  (event: 'update:model-value', newVal: GLCode): void;
}>();

const { t } = useI18n();
const { internalValue } = useModelValue(modelValue, {
  emit,
  factoryMethod: (opts) => new GLCode(opts),
});
const vTooltip = useTooltip();
defineExpose({
  $primevue: usePrimeVue(),
});

const onUpdate = (newVal: GLCode) => {
  const manuallyEdited = newVal.code !== modelValue.value.code;
  // Clear the id if the GL code is not a configured code
  const id = newVal.autoGLCode || manuallyEdited ? undefined : newVal.id;
  const description =
    !newVal.autoGLCode && manuallyEdited
      ? t('glCodes.manuallyEdited')
      : newVal.description;

  internalValue.value = new GLCode({
    ...newVal,
    id,
    description,
    manuallyEdited,
  });
};

const onAcceptSuggestion = () => {
  internalValue.value = new GLCode({
    id: internalValue.value.id,
    code: internalValue.value.suggestion,
    description: internalValue.value.description,
    autoGLCode: true,
    manuallyEdited: false,
  });
};
</script>

<style>
.gl-code-inline-edit {
  --p-chip-background: var(--info-light-color);
  --p-chip-color: var(--info-color);

  gap: var(--sp-2);

  .gl-code-chip {
    height: 2em;

    .popup-gl-code-dropdown-placeholder {
      .gl-code-inline-edit-placeholder {
        max-width: 200px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }

  .suggestion-button {
    .p-button-label {
      text-decoration: none !important;
    }
  }
}

.p-datatable .popup-gl-code-dropdown.popup-inputgroup {
  position: static;
}
</style>
